/**
 * Analytics UI State Slice
 * Handles LOCAL UI state for analytics/dashboard feature (not API calls)
 * API calls are handled by features/analytics/analyticsApiSlice.ts
 */

import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { DailyAnalytics, MonthlyAnalytics, YearlyAnalytics } from "../../types";

// Cache for analytics data to avoid duplicate API calls
// venueId is handled by backend from JWT token
export interface AnalyticsCache {
  data: DailyAnalytics[] | MonthlyAnalytics[] | YearlyAnalytics[] | null;
  timestamp: number | null;
  startDate: string | null;
  endDate: string | null;
}

export interface AnalyticsUIState {
  // Data cache
  dailyCache: AnalyticsCache;
  monthlyCache: AnalyticsCache;
  yearlyCache: AnalyticsCache;

  // Chart preferences
  chartType: "bar" | "line" | "pie" | "area";
  showTrends: boolean;
  compareWithPrevious: boolean;

  // Metrics visibility
  visibleMetrics: {
    totalTickets: boolean;
    totalRevenue: boolean;
    averageTicketValue: boolean;
    vehicleStats: boolean;
    hourlyStats: boolean;
    dailyStats: boolean;
    topVenues: boolean;
    paymentBreakdown: boolean;
    feedbackStats: boolean;
  };

  // Dashboard layout
  layout: "grid" | "list";
  compactMode: boolean;

  // Filters
  filterByVehicleType: "all" | "car" | "bike";
  filterByVenue: string | null;

  // Time grouping for charts
  timeGrouping: "hour" | "day" | "week" | "month";

  // Export preferences
  exportFormat: "csv" | "pdf" | "excel";
  includeCharts: boolean;
}

const initialState: AnalyticsUIState = {
  // Initialize cache
  dailyCache: {
    data: null,
    timestamp: null,
    startDate: null,
    endDate: null,
  },
  monthlyCache: {
    data: null,
    timestamp: null,
    startDate: null,
    endDate: null,
  },
  yearlyCache: {
    data: null,
    timestamp: null,
    startDate: null,
    endDate: null,
  },
  chartType: "bar",
  showTrends: true,
  compareWithPrevious: false,
  visibleMetrics: {
    totalTickets: true,
    totalRevenue: true,
    averageTicketValue: true,
    vehicleStats: true,
    hourlyStats: true,
    dailyStats: true,
    topVenues: true,
    paymentBreakdown: true,
    feedbackStats: true,
  },
  layout: "grid",
  compactMode: false,
  filterByVehicleType: "all",
  filterByVenue: null,
  timeGrouping: "day",
  exportFormat: "csv",
  includeCharts: false,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    // Cache management
    setDailyCache: (
      state,
      action: PayloadAction<{
        data: DailyAnalytics[];
        startDate: string;
        endDate: string;
      }>
    ) => {
      state.dailyCache = {
        data: action.payload.data,
        timestamp: Date.now(),
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    },
    setMonthlyCache: (
      state,
      action: PayloadAction<{
        data: MonthlyAnalytics[];
        startDate: string;
        endDate: string;
      }>
    ) => {
      state.monthlyCache = {
        data: action.payload.data,
        timestamp: Date.now(),
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    },
    setYearlyCache: (
      state,
      action: PayloadAction<{
        data: YearlyAnalytics[];
        startDate: string;
        endDate: string;
      }>
    ) => {
      state.yearlyCache = {
        data: action.payload.data,
        timestamp: Date.now(),
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    },
    clearCache: (state) => {
      state.dailyCache = initialState.dailyCache;
      state.monthlyCache = initialState.monthlyCache;
      state.yearlyCache = initialState.yearlyCache;
    },

    // Chart preferences
    setChartType: (
      state,
      action: PayloadAction<"bar" | "line" | "pie" | "area">
    ) => {
      state.chartType = action.payload;
    },

    toggleTrends: (state) => {
      state.showTrends = !state.showTrends;
    },

    toggleCompareWithPrevious: (state) => {
      state.compareWithPrevious = !state.compareWithPrevious;
    },

    // Metrics visibility
    toggleMetric: (
      state,
      action: PayloadAction<keyof AnalyticsUIState["visibleMetrics"]>
    ) => {
      const metric = action.payload;
      state.visibleMetrics[metric] = !state.visibleMetrics[metric];
    },

    showAllMetrics: (state) => {
      Object.keys(state.visibleMetrics).forEach((key) => {
        state.visibleMetrics[key as keyof typeof state.visibleMetrics] = true;
      });
    },

    hideAllMetrics: (state) => {
      Object.keys(state.visibleMetrics).forEach((key) => {
        state.visibleMetrics[key as keyof typeof state.visibleMetrics] = false;
      });
    },

    // Layout
    setLayout: (state, action: PayloadAction<"grid" | "list">) => {
      state.layout = action.payload;
    },

    toggleCompactMode: (state) => {
      state.compactMode = !state.compactMode;
    },

    // Filters
    setFilterByVehicleType: (
      state,
      action: PayloadAction<"all" | "car" | "bike">
    ) => {
      state.filterByVehicleType = action.payload;
    },

    setFilterByVenue: (state, action: PayloadAction<string | null>) => {
      state.filterByVenue = action.payload;
    },

    clearFilters: (state) => {
      state.filterByVehicleType = "all";
      state.filterByVenue = null;
    },

    // Time grouping
    setTimeGrouping: (
      state,
      action: PayloadAction<"hour" | "day" | "week" | "month">
    ) => {
      state.timeGrouping = action.payload;
    },

    // Export preferences
    setExportFormat: (
      state,
      action: PayloadAction<"csv" | "pdf" | "excel">
    ) => {
      state.exportFormat = action.payload;
    },

    toggleIncludeCharts: (state) => {
      state.includeCharts = !state.includeCharts;
    },

    // Reset to defaults
    resetPreferences: (state) => {
      return initialState;
    },
  },
});

/**
 * Action creators exported for use in components
 * Grouped by functionality for better organization
 */
export const {
  // Cache management
  setDailyCache,
  setMonthlyCache,
  setYearlyCache,
  clearCache,
  // Chart preferences
  setChartType,
  toggleTrends,
  toggleCompareWithPrevious,
  // Metrics visibility
  toggleMetric,
  showAllMetrics,
  hideAllMetrics,
  setLayout,
  toggleCompactMode,
  setFilterByVehicleType,
  setFilterByVenue,
  clearFilters,
  setTimeGrouping,
  setExportFormat,
  toggleIncludeCharts,
  resetPreferences,
} = analyticsSlice.actions;

/**
 * Memoized selectors for optimal performance
 * Use these instead of direct state access
 */

// Base selectors
const selectAnalyticsState = (state: { analytics: AnalyticsUIState }) =>
  state.analytics;

// Cache selectors
export const selectDailyCache = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.dailyCache
);

export const selectMonthlyCache = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.monthlyCache
);

export const selectYearlyCache = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.yearlyCache
);

// Cache validity selectors
export const selectIsDailyCacheValid = createSelector(
  [
    selectDailyCache,
    (_state: any, startDate: string) => startDate,
    (_state: any, _startDate: string, endDate: string) => endDate,
  ],
  (cache, startDate, endDate) => {
    return (
      cache.data &&
      cache.startDate === startDate &&
      cache.endDate === endDate &&
      cache.timestamp &&
      Date.now() - cache.timestamp < 60000 // 1 minute TTL
    );
  }
);

// Chart preferences selectors
export const selectChartType = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.chartType
);

export const selectShowTrends = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.showTrends
);

// Metrics selectors
export const selectActiveMetricsCount = createSelector(
  [selectAnalyticsState],
  (analytics) => Object.values(analytics.visibleMetrics).filter(Boolean).length
);

// Filter selectors
export const selectVehicleFilter = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.filterByVehicleType
);

export const selectVenueFilter = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.filterByVenue
);

// UI preferences selectors
export const selectLayoutMode = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.layout
);

export const selectCompactMode = createSelector(
  [selectAnalyticsState],
  (analytics) => analytics.compactMode
);

// Export preferences selectors
export const selectExportPreferences = createSelector(
  [selectAnalyticsState],
  (analytics) => ({
    format: analytics.exportFormat,
    includeCharts: analytics.includeCharts,
  })
);

/**
 * Composite selector for all analytics UI state
 * Use when multiple values are needed
 */
export const selectAnalyticsUI = createSelector(
  [selectAnalyticsState],
  (analytics) => ({
    chartType: analytics.chartType,
    showTrends: analytics.showTrends,
    layout: analytics.layout,
    compactMode: analytics.compactMode,
    visibleMetrics: analytics.visibleMetrics,
    filters: {
      vehicleType: analytics.filterByVehicleType,
      venue: analytics.filterByVenue,
    },
  })
);

export default analyticsSlice.reducer;
